import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Droplets, ShieldCheck, Sparkles } from "lucide-react";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { RotatingBadge } from "@/components/brand/RotatingBadge";
import { scrollToId } from "@/hooks/useLenis";
import { introDelay as d } from "@/lib/intro";
import { EASE } from "@/lib/motion";

const PILLARS = [
  { icon: ShieldCheck, label: "Deterge e protegge" },
  { icon: Droplets, label: "Nutre e rinforza" },
  { icon: Sparkles, label: "Dona comfort ogni giorno" },
];

const fadeUp = (delay) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.9, ease: EASE } });

const HeroCopy = () => (
  <>
    <motion.p {...fadeUp(d)} className="eyebrow">Dedicata alla generazione d'argento</motion.p>
    <h1 className="mt-6 font-display text-[2.75rem] font-medium leading-[0.98] tracking-[-0.01em] text-navy sm:text-6xl lg:text-7xl" data-testid="hero-title">
      <MaskedLines onLoad delay={d + 0.1} lines={["La cura quotidiana", "per una vita", "più bella", <em className="font-normal italic text-copper">a ogni età.</em>]} />
    </h1>
    <motion.p {...fadeUp(d + 0.6)} className="mt-8 max-w-lg text-lg leading-relaxed text-slate2">
      Perfero è la linea di cura quotidiana pensata per la pelle matura: detersione, trattamento e igiene con postbiotici dell'elicriso. Per sentirsi bene, ogni giorno.
    </motion.p>
    <motion.div {...fadeUp(d + 0.75)} className="mt-10 flex flex-col gap-4 sm:flex-row">
      <Link to="/collezione" data-testid="hero-shop-cta" className="btn-primary group">
        Scopri la collezione <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
      <button type="button" onClick={() => scrollToId("storia")} data-testid="hero-story-cta" className="btn-ghost">
        La nostra storia
      </button>
    </motion.div>
    <motion.ul {...fadeUp(d + 0.9)} className="mt-14 grid gap-4 border-t border-copper/25 pt-8 sm:grid-cols-3">
      {PILLARS.map(({ icon: Icon, label }) => (
        <li key={label} className="flex items-center gap-3 text-[15px] font-medium text-navy">
          <Icon className="h-5 w-5 shrink-0 text-copper" strokeWidth={1.5} /> {label}
        </li>
      ))}
    </motion.ul>
  </>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 70, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 70, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} data-testid="hero-section" className="grain relative overflow-hidden bg-ivory pt-36 lg:pt-40">
      <motion.p aria-hidden="true" style={{ x: wordX }} className="pointer-events-none absolute bottom-[-7vw] left-0 select-none whitespace-nowrap font-brand text-[22vw] leading-none text-transparent [-webkit-text-stroke:1px_rgba(176,112,63,0.2)]">
        PERFERO · PERFERO
      </motion.p>
      <div className="container-pf relative z-10 grid items-center gap-16 pb-28 lg:grid-cols-12 lg:gap-10 lg:pb-36">
        <motion.div style={{ y: textY }} className="lg:col-span-6">
          <HeroCopy />
        </motion.div>
        <div className="relative lg:col-span-6" style={{ perspective: 1400 }}>
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative mx-auto w-full max-w-[540px]">
            <motion.div initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} transition={{ delay: d, duration: 1.5, ease: EASE }} className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] shadow-[0_50px_90px_-45px_rgba(15,30,51,0.55)]">
              <motion.img style={{ y: imgY }} initial={{ scale: 1.4 }} animate={{ scale: 1.16 }} transition={{ delay: d, duration: 2.4, ease: EASE }} src="/images/scene-bath.jpg" alt="La linea Perfero su un piano in marmo, in un bagno luminoso" className="h-full w-full object-cover object-[46%_55%]" data-testid="hero-image" />
              <span className="pointer-events-none absolute inset-3 rounded-t-[999px] border border-ivory/60" />
            </motion.div>
            <motion.div style={{ z: 70 }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d + 0.7, duration: 1.1, ease: EASE }} className="absolute -bottom-10 -left-2 w-[40%] sm:-left-10 lg:-left-16">
              <div className="aspect-square overflow-hidden rounded-full border-[6px] border-ivory shadow-2xl">
                <img src="/images/scene-care.jpg" alt="Una caregiver abbraccia una signora anziana" className="h-full w-full origin-[68%_10%] scale-[2.2] object-cover object-[78%_0%]" />
              </div>
            </motion.div>
            <motion.div style={{ z: 110 }} initial={{ opacity: 0, scale: 0.6, rotate: -40 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: d + 0.9, duration: 1.2, ease: EASE }} className="absolute -right-1 top-[10%] sm:-right-8">
              <RotatingBadge className="h-28 w-28 sm:h-36 sm:w-36" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
